import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useTheme } from "@/components/ui/theme-provider";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Users, MessageSquare, Settings, Share2, Info, Moon, Sun, Search, MoreVertical, Volume2, VolumeX, CircleUser, Trash2, User, Globe } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { ChatRoomInfo, UserInfo } from "@/lib/types";
import { Input } from "@/components/ui/input";

// ✅ ADDED: Importing useToast from the specified hooks path
import { useToast } from "@/hooks/use-toast";
import ConfirmPopup from "@/components/ui/ConfirmPopup";
import { apiUrl } from "@/lib/config";

// PROPS INTERFACE
interface MobileNavProps {
    type: "chat-list" | "chat-room";
    user?: UserInfo | null;
    selectedRoom?: ChatRoomInfo | null;
    memberCount?: number;
    onBack?: () => void;
    onSettings?: (section?: string) => void;
    onOpenLanguageSettings?: () => void;
    onRoomInfo?: () => void;
    onShareInvite?: () => void;
    onCall?: () => void;
    onVideoCall?: () => void;
    onCreateChat: () => void;
    onCreateGroup: () => void;
    autoTranslationEnabled?: boolean;
    onAutoTranslationChange?: (enabled: boolean) => void;
    userLanguage?: string;

    // Search Props
    isSearching: boolean;
    onToggleSearch: () => void;
    searchQuery: string;
    onSearchQueryChange: (query: string) => void;
    onSearchMessages?: () => void;
    isMessageSearching?: boolean;
    messageSearchQuery?: string;
    onMessageSearchQueryChange?: (query: string) => void;
    onCloseMessageSearch?: () => void;
    onRoomDeleted?: () => void;
    onClearCurrentRoom?: () => void;
}

export function MobileNav({ type, user, selectedRoom, memberCount = 0, onBack, onSettings, onOpenLanguageSettings, onRoomInfo, onShareInvite, onCall, onVideoCall, onCreateChat, onCreateGroup, autoTranslationEnabled = true, onAutoTranslationChange, userLanguage = "en", isSearching, onToggleSearch, searchQuery, onSearchQueryChange, onSearchMessages, isMessageSearching = false, messageSearchQuery = "", onMessageSearchQueryChange, onCloseMessageSearch, onRoomDeleted, onClearCurrentRoom }: MobileNavProps) {
    const { theme, setTheme } = useTheme();

    // State for Mute functionality
    const [isMuted, setIsMuted] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [actionType, setActionType] = useState<"deleteGroup" | "deleteChat" | null>(null);
    const [isUserOnline, setIsUserOnline] = useState(true);

    // Initialize Toast
    const { toast } = useToast();

    // --- Mute Status Persistence (The Fix) ---
    // Loads mute status from localStorage when the room or view type changes
    useEffect(() => {
        // Only load status if we are in the 'chat-room' view and a room is selected
        if (type === "chat-room" && selectedRoom?.roomId) {
            // Key format: chatMuteStatus_[roomId]
            const muteStatus = localStorage.getItem(`chatMuteStatus_${selectedRoom.roomId}`);
            setIsMuted(muteStatus === "true");
        } else if (type === "chat-list") {
            // Ensure it's reset or off when viewing the chat list
            setIsMuted(false);
        }
    }, [selectedRoom?.roomId, type]);

    // Track user online status
    // useEffect(() => {
    //     const updateOnlineStatus = () => setIsUserOnline(navigator.onLine);
    //     const handleActivity = () => setIsUserOnline(true);

    //     window.addEventListener('online', updateOnlineStatus);
    //     window.addEventListener('offline', updateOnlineStatus);
    //     window.addEventListener('focus', handleActivity);
    //     window.addEventListener('mousemove', handleActivity);
    //     window.addEventListener('keypress', handleActivity);

    //     return () => {
    //         window.removeEventListener('online', updateOnlineStatus);
    //         window.removeEventListener('offline', updateOnlineStatus);
    //         window.removeEventListener('focus', handleActivity);
    //         window.removeEventListener('mousemove', handleActivity);
    //         window.removeEventListener('keypress', handleActivity);
    //     };
    // }, []);

    const toggleTheme = () => {
        setTheme(theme === "light" ? "dark" : "light");
    };
    // delete popup logic
    const handleConfirm = async () => {
        if (!selectedRoom || !user) return;

        try {
            const response = await fetch(apiUrl(`/api/rooms/${selectedRoom.roomId}`), {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: user.uid })
            });

            const result = await response.json();

            if (response.ok) {
                const isLeaving = result.message.includes("Left room");
                toast({
                    title: isLeaving ? "Left Room" : (actionType === "deleteGroup" ? "Group Archived" : "Chat Archived"),
                    description: result.message,
                    variant: "default"
                });
                onRoomDeleted?.();
                onBack?.();
            } else {
                toast({
                    title: "Error",
                    description: result.error,
                    variant: "destructive"
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to delete room",
                variant: "destructive"
            });
        }
        setIsPopupOpen(false);
        setActionType(null);
    };

    const handleCancel = () => {
        setIsPopupOpen(false);
        setActionType(null);
    };

    const isCreator = selectedRoom?.createdBy === user?.uid;
    const popupConfig =
        actionType === "deleteGroup"
            ? {
                title: isCreator ? "Delete Group?" : "Leave Group?",
                message: isCreator ? "This will archive the group for all members" : "You will leave this group and won't receive new messages",
                confirmText: isCreator ? "Yes, delete group" : "Yes, leave group",
            }
            : actionType === "deleteChat"
                ? {
                    title: isCreator ? "Delete Chat?" : "Leave Chat?",
                    message: isCreator ? "This will archive the chat for all members" : "You will leave this chat and won't receive new messages",
                    confirmText: isCreator ? "Yes, delete chat" : "Yes, leave chat",
                }
                : null;

    // --- Toggle Mute Function ---
    const toggleMute = () => {
        if (!selectedRoom?.roomId) return; // Safety check

        const newMutedStatus = !isMuted;
        setIsMuted(newMutedStatus);

        const shortName = selectedRoom.name ? (selectedRoom.name.length > 15 ? selectedRoom.name.slice(0, 12) + "..." : selectedRoom.name) : "this chat";

        // Persist the new status to Local Storage
        localStorage.setItem(`chatMuteStatus_${selectedRoom.roomId}`, newMutedStatus.toString());

        // Show Toast Message
        toast({
            title: newMutedStatus ? "Chat Muted" : "Chat Unmuted",
            description: newMutedStatus ? `Notifications for "${shortName}" are off.` : `Notifications for "${shortName}" are on.`,
            // variant: newMutedStatus ? "default" : "success",
        });
    };



    if (type === "chat-list") {
        return (
            <div className="sticky top-0 left-0 right-0 z-50 bg-white dark:bg-[#0f172a] backdrop-blur-sm border-b border-border px-4 py-3" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
                <div className="flex items-center justify-between">
                    {/* Conditional Rendering: Show Search Input OR Header Title */}
                    {isSearching ? (
                        // === Search Input View ===
                        <div className="flex items-center w-full space-x-2">
                            {/* 1. Close Button (Back) */}
                            <Button variant="ghost" size="icon" onClick={onToggleSearch} className="hover:bg-[var(--accent)] hover:text-white transition-colors">
                                <ArrowLeft className="w-5 h-5" />
                            </Button>

                            {/* 2. Search Input Field */}
                            <Input placeholder="Search chats..." className="flex-grow dark:bg-black dark:text-white" value={searchQuery} onChange={(e) => onSearchQueryChange(e.target.value)} autoFocus />
                        </div>
                    ) : (
                        // === Default Header View ===
                        <>
                            {/* Header Title: Vyzle */}
                            <div className="flex items-center">
                                <img src="/Images/logo-light.png" alt="Vyzle" className="h-8 block dark:hidden" />
                                <img src="/Images/logo-dark.png" alt="Vyzle" className="h-8 hidden dark:block" />
                            </div>

                            {/* Icons: Search and More Options */}
                            <div className="flex items-center space-x-1.5 sm:space-x-2">
                                {/* Search Icon */}
                                <Button variant="ghost" size="icon" onClick={onToggleSearch} className="hover:bg-[var(--accent)] hover:text-white transition-colors">
                                    <Search className="w-5 h-5" />
                                </Button>

                                {/* More Options Dropdown */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="hover:bg-[var(--accent)] hover:text-white transition-colors">
                                            <MoreVertical className="w-5 h-5" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                        <DropdownMenuItem onClick={() => onSettings?.("profile")} className="cursor-pointer font-medium">
                                            <CircleUser className="w-5 h-5 mr-2 text-[var(--accent)]" />
                                            <span className="truncate">{user?.displayName || "Anonymous"}</span>
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
                                            {theme === "light" ? <Moon className="w-4 h-4 mr-2" /> : <Sun className="w-4 h-4 mr-2" />}
                                            {theme === "light" ? "Dark Mode" : "Light Mode"}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => onSettings?.("profile")} className="cursor-pointer">
                                            <Settings className="w-4 h-4 mr-2" />
                                            Settings
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // === 'chat-room' view (Mute functionality is applied here) ===
    return (
        <>
            <div className="fixed top-0 left-0 right-0 z-50  bg-white dark:bg-[#0f172a] backdrop-blur-sm border-b border-border px-4 py-3" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
                <div className="flex items-center justify-between">
                    {isMessageSearching ? (
                        // === Message Search Input View ===
                        <div className="flex items-center w-full space-x-2">
                            <Button variant="ghost" size="icon" onClick={onCloseMessageSearch} className="hover:bg-[var(--accent)] hover:text-white transition-colors">
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <Input
                                placeholder="Search messages..."
                                className="flex-grow dark:bg-black dark:text-white"
                                value={messageSearchQuery}
                                onChange={(e) => onMessageSearchQueryChange?.(e.target.value)}
                                autoFocus
                            />
                        </div>
                    ) : (
                        // === Default Chat Room Header ===
                        <>
                            <div className="flex items-center space-x-3">
                                <Button variant="ghost" size="icon" onClick={onBack} className="hover:bg-[var(--accent)] hover:text-white transition-colors">
                                    <ArrowLeft className="w-5 h-5" />
                                </Button>

                                <div className="flex items-center space-x-3 flex-1 cursor-pointer" onClick={onRoomInfo}>
                                    <Avatar className="w-10 h-10">
                                        <AvatarFallback className="bg-[var(--accent)] text-white">{selectedRoom?.type === "group" ? <Users className="w-5 h-5" /> : <User className="w-5 h-5" />}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-lg font-semibold truncate">{selectedRoom?.name ?? "Chat"}</h2>
                                        <div className="flex items-center space-x-1">
                                            <div className="w-2 h-2 bg-[var(--accent)] rounded-full" />
                                            <span className="text-xs text-muted-foreground">
                                                Connected securely
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </>
                    )}

                    {!isMessageSearching && (
                        <div className="flex items-center space-x-1.5">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onShareInvite}
                                className="hover:bg-[var(--bubble-own-to)] text-[var(--bubble-own-to)] hover:text-white flex"
                            >
                                <Share2 className="w-5 h-4" />
                            </Button>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="hover:bg-[var(--accent)] hover:text-white transition-colors">
                                        <MoreVertical className="w-5 h-5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem onClick={onRoomInfo}>
                                        <Info className="w-4 h-4 mr-2" />
                                        Room Info
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={onShareInvite}>
                                        <Share2 className="w-4 h-4 mr-2" />
                                        Share Invite
                                    </DropdownMenuItem>

                                    {/* MUTE/UNMUTE OPTION (Uses isMuted state) */}
                                    <DropdownMenuItem onClick={toggleMute}>
                                        {isMuted ? <VolumeX className="w-4 h-4 mr-2" /> : <Volume2 className="w-4 h-4 mr-2" />}
                                        {isMuted ? "Unmute" : "Mute"}
                                    </DropdownMenuItem>

                                    <DropdownMenuItem onClick={onSearchMessages}>
                                        <Search className="w-4 h-4 mr-2" />
                                        Search Messages
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                        onSelect={(e) => {
                                            e.preventDefault();
                                            onAutoTranslationChange?.(!autoTranslationEnabled);
                                        }}
                                        className="flex items-center justify-between cursor-pointer focus:bg-transparent focus:text-foreground hover:bg-transparent hover:text-foreground active:bg-transparent data-[highlighted]:bg-transparent data-[highlighted]:text-foreground"
                                    >
                                        <div className="flex items-center whitespace-nowrap">
                                            <Globe className="w-4 h-4 mr-2 shrink-0" />
                                            <span>Auto Translate</span>
                                        </div>
                                        <Switch
                                            checked={autoTranslationEnabled}
                                            onCheckedChange={(checked) => onAutoTranslationChange?.(checked)}
                                            className="scale-75 origin-right data-[state=checked]:bg-[var(--accent)] pointer-events-none ml-2"
                                            aria-label="Toggle auto translation"
                                        />
                                    </DropdownMenuItem>

                                    {onSettings && (
                                        <DropdownMenuItem onClick={() => onSettings("profile")} className="cursor-pointer">
                                            <Settings className="w-4 h-4 mr-2" />
                                            Settings
                                        </DropdownMenuItem>
                                    )}

                                    <DropdownMenuSeparator />
                                    {selectedRoom?.type === "direct" && (
                                        <DropdownMenuItem
                                            onClick={() => {
                                                setActionType("deleteChat");
                                                setIsPopupOpen(true);
                                            }}
                                            className="hover:text-white"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" /> Exit Chat
                                        </DropdownMenuItem>
                                    )}

                                    {selectedRoom?.type === "group" && (
                                        <DropdownMenuItem
                                            onClick={() => {
                                                setActionType("deleteGroup");
                                                setIsPopupOpen(true);
                                            }}
                                            className=" hover:text-white"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" /> Exit Group
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}
                </div>
            </div>

            {isPopupOpen && popupConfig && <ConfirmPopup isOpen={isPopupOpen} title={popupConfig.title} message={popupConfig.message} confirmText={popupConfig.confirmText} cancelText="Cancel" onConfirm={handleConfirm} onCancel={handleCancel} />}
        </>
    );
}
